import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/apkros";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const apkros: CaseStudy = {
  slug: "apkros",
  title: "Арктический Перевалочный Комплекс",
  cardTitle: "Разработка сайта Арктического Перевалочного Комплекса",
  subtitle: "Грузовая и морская логистика в Арктике",
  industry: { label: "Логистика и промышленность", slug: "logistics" },
  eyebrow: "Корпоративный сайт · Арктическая и морская логистика",
  h1: "Разработка сайта Арктического Перевалочного Комплекса",
  lead:
    "Разработали корпоративный B2B-сайт Арктического Перевалочного Комплекса: грузовая и морская логистика, топливная инфраструктура и работа в условиях Арктики.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить B2B-сайт" },

  seo: {
    primaryKeyword: "разработка корпоративного сайта логистического комплекса",
    secondaryKeywords: [
      "сайт логистического терминала",
      "корпоративный сайт промышленной логистики",
      "сайт портового комплекса",
      "разработка B2B сайта логистики",
    ],
    semanticEntities: [
      "перевалка грузов",
      "морская логистика",
      "топливная инфраструктура",
      "бункеровка",
      "снабжение удалённых объектов",
      "Арктика",
    ],
    excludedKeywords: [
      "перевалка грузов цена",
      "бункеровка судов",
      "доставка в Арктику",
      "нефтепродукты Мурманск",
    ],
    title: "Сайт Арктического Перевалочного Комплекса — кейс HAY.agency",
    description:
      "Кейс HAY.agency: корпоративный B2B-сайт АПК о грузовой и морской логистике, топливной инфраструктуре и работе в условиях Арктики.",
    h1: "Разработка сайта Арктического Перевалочного Комплекса",
    slug: "apkros",
    canonicalUrl: `${SITE_URL}/cases/apkros`,
    ogTitle: "Сайт Арктического Перевалочного Комплекса",
    ogDescription:
      "Грузовая и морская логистика, топливная инфраструктура и работа в условиях Арктики.",
    ogImage: `${SITE_URL}${dir}/apkros-og.webp`,
    robotsIndex: true,
    datePublished: "2024-05-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "B2B-структура и контактный блок",
    },
    relatedIndustry: {
      label: "Сайты для логистики и промышленности",
      to: routes.corporate,
      note: "Грузовая логистика и промышленная инфраструктура",
    },
    relatedCases: [],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен B2B-сайт для логистического комплекса?",
    text: "Спроектируем структуру перевалки грузов, топливного блока и контактов для бизнеса.",
    button: "Обсудить B2B-сайт",
  },

  client: {
    name: "Арктический Перевалочный Комплекс",
    url: "https://apkros.ru/",
    city: "Мурманск",
    industry: "Арктическая и морская логистика",
  },
  year: "2024",
  services: [
    "Дизайн корпоративного сайта",
    "Разработка B2B-структуры",
    "Раздел топливного блока",
    "Контактный раздел",
  ],
  platform: "Custom",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Арктический Перевалочный Комплекс" },
    { label: "Отрасль", value: "Арктическая и морская логистика" },
    { label: "Тип проекта", value: "B2B-сайт логистического комплекса" },
    { label: "Платформа", value: "Custom" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Раздел полного цикла перевалки грузов и морской логистики",
      "Описание перевалочного комплекса в Мурманске",
      "Топливный блок: бункеровка, хранение и приём нефтепродуктов",
      "Раздел снабжения удалённых объектов, учёта, маркировки и документации",
      "Контактный раздел с реквизитами компании",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Арктический Перевалочный Комплекс управляет специализированной инфраструктурой в Мурманске: перевалкой грузов, морской логистикой и топливным блоком. Нужен был корпоративный сайт, который понятно объясняет бизнесу масштаб и специфику работы в условиях Арктики.",
        "Вторая часть задачи — показать несколько направлений работы компании как единую систему: грузовую логистику, топливную инфраструктуру и снабжение удалённых объектов.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: холодная промышленная подача",
    paragraphs: [
      "Интерфейс собран на холодной arctic graphite/steel палитре — подача соответствует промышленному B2B-проекту, а не рекламному сайту.",
      "Структура сайта строится вокруг реальной инфраструктуры компании: порт, суда, топливный блок и документация.",
    ],
    bullets: [
      "Холодная графитовая палитра вместо белого фона",
      "Реальная инфраструктура вместо стоковых изображений",
      "Компактный контактный блок без перегруженной формы",
    ],
  },

  identity: {
    fonts: [
      { name: "Golos Text", role: "Заголовки", sample: "Арктический Перевалочный Комплекс" },
      { name: "Golos Text", role: "Текст и разделы", sample: "Топливный блок" },
    ],
    colors: [
      { hex: "#EDEFF0", name: "Основной фон" },
      { hex: "#2B3A45", name: "Акцент бренда" },
      { hex: "#14181B", name: "Текст" },
    ],
  },

  cover: img(
    "apkros-case-cover",
    "Главная страница сайта Арктического Перевалочного Комплекса",
    6,
  ),

  gallery: [
    img("apkros-cargo-flow", "Схема перевалки грузов Арктического Перевалочного Комплекса", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия сохраняет структуру разделов: логистика, топливный блок и контакты доступны без потери контекста.",
      "Смысловые блоки на мобильном идут горизонтальным скроллом, hero собран отдельной композицией.",
    ],
    images: [
      mobile("apkros-mobile-hero", "Мобильная главная сайта Арктического Перевалочного Комплекса"),
      mobile("apkros-mobile-contacts", "Мобильный контактный раздел Арктического Перевалочного Комплекса"),
    ],
  },

  columnBlocks: [
    {
      id: "arctic",
      title: "Арктическая специфика",
      paragraphs: [
        "Один сильный блок объясняет географию и инфраструктуру компании: перевалочный комплекс работает в условиях Арктики и обслуживает удалённые объекты.",
      ],
      bullets: ["География и инфраструктура", "Работа в условиях Арктики"],
    },
    {
      id: "cargo",
      title: "Грузовая логистика",
      paragraphs: [
        "Полный цикл перевалки грузов и морская доставка показаны как последовательность этапов — от приёма груза до доставки конечному объекту.",
      ],
      columns: [
        { title: "Приём", items: ["Приём груза", "Учёт и маркировка"] },
        { title: "Перевалка", items: ["Перевалочный комплекс", "Морская доставка"] },
      ],
    },
    {
      id: "fuel",
      title: "Топливный блок",
      paragraphs: [
        "Бункеровка, хранение и приём нефтепродуктов выделены в отдельное направление работы компании.",
      ],
      bullets: ["Бункеровка судов", "Хранение нефтепродуктов", "Приём нефтепродуктов"],
    },
    {
      id: "contacts",
      title: "B2B-контакт",
      paragraphs: [
        "Контакты и реквизиты компании собраны в отдельном разделе с прямым переходом к обсуждению задачи.",
      ],
      bullets: ["Контакты и реквизиты", "Переход к обсуждению задачи"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
    ],
  },
};
